import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../context/AuthContext";
import { Trophy, Flame, TrendingUp } from "lucide-react";

const PERIODS = [
  { id: "day", label: "24h" },
  { id: "week", label: "7d" },
  { id: "month", label: "30d" },
  { id: "all", label: "All time" },
];

export default function Leaderboard() {
  const [period, setPeriod] = useState("week");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/leaderboard?period=${period}&limit=10`)
      .then((r) => setRows(r.data.leaderboard || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <section id="leaderboard" className="relative px-6 md:px-10 py-24">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[12px] uppercase tracking-[0.25em] text-white/40 inline-flex items-center gap-2">
              <Flame className="w-3.5 h-3.5" /> Hot right now
            </div>
            <h2 className="mt-3 text-[44px] md:text-[60px] font-black tracking-[-0.03em] leading-[1]">Biggest hits.</h2>
          </div>
          <div className="glass rounded-full p-1 inline-flex">
            {PERIODS.map((p) => (
              <button key={p.id} onClick={() => setPeriod(p.id)}
                className={`px-4 py-1.5 rounded-full text-[12px] transition-colors ${period === p.id ? "bg-white text-black" : "text-white/70 hover:text-white"}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 glass rounded-3xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-white/40 text-[14px]">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-white/40 text-[14px]">No wins in this window yet. Be the first.</div>
          ) : (
            <table className="w-full text-[14px]">
              <thead className="text-[11px] uppercase tracking-widest text-white/40">
                <tr>
                  <th className="text-left p-4 w-14">#</th>
                  <th className="text-left p-4">Player</th>
                  <th className="text-left p-4 hidden md:table-cell">Game</th>
                  <th className="text-right p-4">Wager</th>
                  <th className="text-right p-4">Multiplier</th>
                  <th className="text-right p-4">Payout</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.bet_id + i} className="border-t hairline hover:bg-white/[0.02]">
                    <td className="p-4 font-semibold">
                      {i === 0 ? <Trophy className="w-4 h-4 text-[#FFD166]" /> :
                       i === 1 ? <Trophy className="w-4 h-4 text-white/70" /> :
                       i === 2 ? <Trophy className="w-4 h-4 text-[#c08457]" /> :
                       <span className="text-white/40">{i + 1}</span>}
                    </td>
                    <td className="p-4">
                      {r.fake ? (
                        <div className="flex items-center gap-2">
                          {r.avatar ? <img src={r.avatar} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-white/10" />}
                          <div className="leading-tight">
                            <div className="text-white">{r.global_name || r.username}</div>
                            {r.stake_username && <div className="text-[11px] text-white/40">@{r.stake_username}</div>}
                          </div>
                        </div>
                      ) : (
                        <Link to={`/p/${encodeURIComponent(r.stake_username || r.username)}`} className="flex items-center gap-2 hover:opacity-80">
                          {r.avatar ? <img src={r.avatar} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-white/10" />}
                          <div className="leading-tight">
                            <div className="text-white">{r.global_name || r.username}</div>
                            {r.stake_username && <div className="text-[11px] text-white/40">@{r.stake_username}</div>}
                          </div>
                        </Link>
                      )}
                    </td>
                    <td className="p-4 hidden md:table-cell text-white/80">{r.game}</td>
                    <td className="p-4 text-right text-white/70">${(r.wager || 0).toFixed(2)}</td>
                    <td className="p-4 text-right text-white font-semibold inline-flex items-center gap-1 justify-end w-full">
                      <TrendingUp className="w-3.5 h-3.5" />{(r.multiplier || 0).toFixed(1)}x
                    </td>
                    <td className="p-4 text-right font-semibold">${(r.payout || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
}
